import React from "react";
import { Form } from "react-final-form";
import { FORM_ERROR } from "final-form";
import createDecorator from "final-form-focus";
import { useIntl, FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { Button } from "@ebsco-ui/ebsco-ui";
import { PasswordInputField as ConfirmPasswordField } from "../../shared/fields";
import CoppaStrategy from "../../coppa-strategy";
import sharedMessages from "../../../../shared-messages";
import mapApiErrorsToFormErrors from "../../../utils/map-api-errors-to-field-errors";
import submitErrorFieldsHaveChanged from "../../../utils/submit-error-fields-have-changed";
import {
  getUsername,
  getVerificationData,
  resetPasswordRequest,
  resetPasswordSuccess,
  resetPasswordFailure,
  clearResetPasswordState
} from "../../../store/password/reset";
import FormErrorNotification from "../../form-error-notification";
import { useEdgeApiContext } from "../../edge-api";
import {
  NonCoppaResetPasswordFormFields,
  CoppaResetPasswordFormFields
} from "../reset-password-form-types";
import messages from "./reset-password-form-messages";
import submitMessages from "./submit-error-message-descriptors";

import "./reset-password-form.scss";

const focusOnErrors = createDecorator();

const block = "reset-password-form";

const ResetPasswordForm = props => {
  const personalUserApi = useEdgeApiContext();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const username = useSelector(getUsername);
  const verificationData = useSelector(getVerificationData);

  // yeah this is duplicated from the update password form....
  const confirmPasswordValidationSchema = yup.object({
    value: yup
      .string()
      .required(formatMessage(sharedMessages.requireConfirmPassword))
      .test(
        "match-new-password",
        formatMessage(sharedMessages.passwordsDoNotMatch),
        function(value) {
          return value === this.parent.allFormValues.newPassword;
        }
      )
  });

  const handleStartOverClick = () => dispatch(clearResetPasswordState());

  const onSubmit = async ({ newPassword }) => {
    dispatch(resetPasswordRequest());
    try {
      await personalUserApi.postPassword(
        newPassword,
        verificationData,
        username
      );
      dispatch(resetPasswordSuccess());
    } catch (error) {
      dispatch(resetPasswordFailure(error));
      let formErrors = {
        [FORM_ERROR]: {
          headerMessage: formatMessage(sharedMessages.somethingWentWrong),
          descriptionMessage: formatMessage(messages.unableToResetPassword)
        }
      };

      if (error.response) {
        const {
          response: { status, data }
        } = error;

        if (status === 400) {
          const mappedErrors = mapApiErrorsToFormErrors(
            data.details,
            key => submitMessages[key]
          );

          if (mappedErrors.verificationData) {
            formErrors[FORM_ERROR].descriptionMessage = (
              <FormattedMessage
                {...mappedErrors.verificationData[0]}
                values={{
                  button: displayButton(handleStartOverClick, block)
                }}
              />
            );
          }
        }
      }

      return formErrors;
    }
  };

  return (
    <div className={block}>
      <Form onSubmit={onSubmit} decorators={[focusOnErrors]}>
        {({
          handleSubmit,
          submitting,
          submitFailed,
          submitErrors,
          submitError,
          dirtyFieldsSinceLastSubmit,
          dirty
        }) => {
          const disableSave =
            submitting ||
            !(submitFailed
              ? submitErrorFieldsHaveChanged(
                  submitErrors,
                  dirtyFieldsSinceLastSubmit
                )
              : dirty);

          return (
            <form onSubmit={handleSubmit}>
              {submitError && (
                <FormErrorNotification
                  className={`${block}__form-error`}
                  {...submitError}
                />
              )}
              <CoppaStrategy
                username={username}
                coppaComponent={CoppaResetPasswordFormFields}
                nonCoppaComponent={NonCoppaResetPasswordFormFields}
              />
              <ConfirmPasswordField
                id="confirm-password"
                name="confirmPassword"
                label={formatMessage(sharedMessages.confirmPassword)}
                aria-required="true"
                validationSchema={confirmPasswordValidationSchema}
              />
              <Button
                className={`${block}__button`}
                type="submit"
                disabled={disableSave}
                styleType="solid"
              >
                <FormattedMessage {...sharedMessages.resetPassword} />
              </Button>
            </form>
          );
        }}
      </Form>
    </div>
  );
};

const displayButton = (handleStartOverClick, block) => msg => (
  <Button
    onClick={handleStartOverClick}
    className={`${block}__start-over-button`}
    type="button"
    styleType="link"
  >
    {msg}
  </Button>
);

export default ResetPasswordForm;
